import { Content, ContentVariants, Popover } from "@patternfly/react-core";
import { HelpIcon, InfoCircleIcon } from "@patternfly/react-icons";
import { FormContainer } from "@rhc-shared-components/form";
import { FormCheckbox } from "@rhc-shared-components/form-checkbox";
import { FormSelect } from "@rhc-shared-components/form-select-component";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { Formik } from "formik";
import { type FC, forwardRef } from "react";
import { ProjectHelmChartKeys, ProjectTypes } from "../../../../constants/project";
import { useCreateModalStore } from "../CreateModalStore";
import {
	CreateFormKeys,
	DistributionMethodTypeHelm,
	HelmCreateInitialValues,
	HelmCreateValidator,
	HelmProjectDistributionMethod,
	ProjectCreateModalKeys,
	useCreateAndAttachComponent,
} from "../createConstants";

interface HelmChartCreateFormProps {
	handleCreateModal: (showModal: boolean) => void;
	ref: any;
}

export const HelmChartCreateForm: FC<HelmChartCreateFormProps> = forwardRef((props, ref: any) => {
	const { createNewComponent } = useCreateAndAttachComponent(props.handleCreateModal, "new");
	const { modalData } = useCreateModalStore();
	return (
		<Formik
			innerRef={ref}
			initialValues={HelmCreateInitialValues}
			validationSchema={HelmCreateValidator}
			onSubmit={async (values) => {
				let distributionMethod: string;
				if (values[CreateFormKeys.distributionMethod] === HelmProjectDistributionMethod.redhat) {
					distributionMethod = values[CreateFormKeys.helmDistributionMethodType] ? "external" : "redhat";
				} else {
					distributionMethod = "undistributed";
				}
				const requestData = {
					[ProjectCreateModalKeys.name]: modalData.componentName,
					[ProjectCreateModalKeys.helmChart]: {
						[ProjectHelmChartKeys.chartName]: values[CreateFormKeys.chartName],
						[ProjectHelmChartKeys.distributionMethod]: distributionMethod,
					},
					[ProjectCreateModalKeys.marketplace]: {
						enablement_status: "ineligible",
					},
					[ProjectCreateModalKeys.type]: ProjectTypes.helmChart,
					[ProjectCreateModalKeys.projectStatus]: "active",
				};
				await createNewComponent(requestData);
			}}
		>
			{({ values }) => (
				<FormContainer gridSpan={12}>
					<Content component={ContentVariants.small} style={{ color: "#777", marginBottom: "-16px" }}>
						<InfoCircleIcon color="#2b9af3" />
						&ensp; The following values can't be changed after the project has been created.
					</Content>
					<FormTextInput
						name={CreateFormKeys.chartName}
						labelIcon={
							<Popover
								className={"component-popover"}
								bodyContent={<div>This name must match the name of the chart submitted through GitHub.</div>}
							>
								<button
									type="button"
									aria-label="more info"
									onClick={(e) => e.preventDefault()}
									className="pf-c-form__group-label-help"
								>
									<HelpIcon />
								</button>
							</Popover>
						}
						label="Chart Name"
						helperText="Chart name must be unique. Must be lower case letters or numbers, and start with a letter. Words may be separated with dashes (-)"
						isRequired
						data-testid="chart-name"
					/>
					<FormSelect
						name={CreateFormKeys.distributionMethod}
						isFullWidth
						placeholder="Select repository"
						label="Distribution Method"
						subLabel={
							<Content>
								<Content component="p" className={"sub-label"}>
									Select the Helm chart repository where your charts will be located
								</Content>
							</Content>
						}
						selectOptions={DistributionMethodTypeHelm}
						isRequired
					/>
					{values[CreateFormKeys.distributionMethod] === HelmProjectDistributionMethod.redhat && (
						<FormCheckbox
							id="helm-chart-checkbox"
							data-testid="helm-chart-checkbox"
							name={CreateFormKeys.helmDistributionMethodType}
							label=""
							subLabel="The certified helm chart will be distributed from my company's repository"
						/>
					)}
				</FormContainer>
			)}
		</Formik>
	);
});
